import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Chip, Avatar,
  Skeleton, useTheme, alpha, IconButton, Tooltip,
  Stack, useMediaQuery
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Refresh, SportsBaseball
} from '@mui/icons-material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { Collapse, Button, Divider } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { gamesApi } from '../services/apiService';
import { matchupsApi } from '../services/apiService';
import axios from 'axios';
import Boxscore from './Boxscore';
// GameAnalyticsPanel no longer used — games open directly in Boxscore dialog
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

// ── Team primary colors (MLB App style accent bars) ───────────────────────────
const TEAM_COLORS = {
  ARI:'#A71930', ATL:'#CE1141', BAL:'#DF4601', BOS:'#BD3039', CHC:'#0E3386',
  CWS:'#27251F', CIN:'#C6011F', CLE:'#00385D', COL:'#333366', DET:'#0C2340',
  HOU:'#002D62', KCR:'#004687', LAA:'#BA0021', LAD:'#005A9C', MIA:'#00A3E0',
  MIL:'#FFC52F', MIN:'#002B5C', NYM:'#002D72', NYY:'#003087', OAK:'#003831',
  PHI:'#E81828', PIT:'#FDB827', SDP:'#7F411C', SFG:'#FD5A1E', SEA:'#0C2C56',
  STL:'#C41E3A', TBR:'#092C5C', TEX:'#003278', TOR:'#134A8E', WSN:'#AB0003',
};
const getTeamColor = (abbr) => TEAM_COLORS[abbr] || TEAM_COLORS[(abbr||'').toUpperCase()] || '#666';

const getTeamLogoUrl = (teamCode) => getSharedTeamLogoUrl(teamCode, 500);

// ── Date helpers ──────────────────────────────────────────────────────────────
const toDateStr = (d) =>
  d.getFullYear() + '-' +
  String(d.getMonth() + 1).padStart(2, '0') + '-' +
  String(d.getDate()).padStart(2, '0');

const parseLocalDate = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (str, n) => {
  const d = parseLocalDate(str);
  d.setDate(d.getDate() + n);
  return toDateStr(d);
};

const formatDayLabel = (dateStr, todayStr) => {
  if (dateStr === todayStr) return 'Today';
  if (dateStr === addDays(todayStr, -1)) return 'Yesterday';
  if (dateStr === addDays(todayStr, 1)) return 'Tomorrow';
  const d = parseLocalDate(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatGameTime = (gameTime) => {
  if (!gameTime) return 'TBD';
  try {
    return new Date(gameTime).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch { return 'TBD'; }
};

// ══════════════════════════════════════════════════════════════════════════════
// CAREER MATCHUP PANEL — inline H2H summary for scheduled games
// ══════════════════════════════════════════════════════════════════════════════
const CareerMatchupPanel = ({ game }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const homeAbbr = game.homeTeam?.abbreviation;
  const awayAbbr = game.awayTeam?.abbreviation;
  const homePitcher = game.probableHomePitcher;
  const awayPitcher = game.probableAwayPitcher;
  const canShow = homeAbbr && awayAbbr && (homePitcher || awayPitcher);

  const handleToggle = async (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && !data && canShow) {
      setLoading(true);
      setError(null);
      try {
        const out = {};
        if (homePitcher) {
          try {
            out.awayVsHomePitcher = await matchupsApi.getGamePreview(
              awayAbbr, homeAbbr, `${homeAbbr}-${homePitcher.replace(/\s+/g, '_')}`
            );
          } catch (_) {}
        }
        if (awayPitcher) {
          try {
            out.homeVsAwayPitcher = await matchupsApi.getGamePreview(
              homeAbbr, awayAbbr, `${awayAbbr}-${awayPitcher.replace(/\s+/g, '_')}`
            );
          } catch (_) {}
        }
        setData(out);
      } catch (err) {
        setError('Unable to load matchups');
      } finally {
        setLoading(false);
      }
    }
  };

  if (!canShow) return null;

  const renderTopBatters = (preview, label) => {
    if (!preview) return null;
    const list = (preview.careerBatters || []).slice(0, 5);
    if (list.length === 0) {
      return (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled', display: 'block' }}>
            No career H2H data
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ py: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Stack spacing={0.25}>
          {list.map((b) => {
            const pa  = b.stats?.plateAppearances || 0;
            const ab  = b.stats?.atBats || 0;
            const hr  = b.stats?.homeRuns || 0;
            const avg = b.stats?.avg != null ? Number(b.stats.avg).toFixed(3).replace(/^0/, '') : '—';
            const ops = b.stats?.ops != null ? Number(b.stats.ops).toFixed(3).replace(/^0/, '') : '—';
            return (
              <Stack key={b.name} direction="row" alignItems="center" justifyContent="space-between"
                sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                  {b.name}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {ab}AB · {hr}HR · {avg}/{ops} <span style={{ opacity: 0.6 }}>({pa}PA)</span>
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 0.75 }}>
      <Button
        size="small"
        onClick={handleToggle}
        endIcon={open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        sx={{
          py: 0.25, px: 1, minHeight: 'auto',
          fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
          color: 'text.secondary',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
        }}
      >
        Career Matchups
      </Button>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box onClick={(e) => e.stopPropagation()}
          sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.divider, 0.06) }}>
          {loading && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
              Loading career H2H…
            </Typography>
          )}
          {error && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'error.main' }}>
              {error}
            </Typography>
          )}
          {!loading && !error && data && (
            <Stack divider={<Divider flexItem sx={{ my: 0.25 }} />} spacing={0.5}>
              {renderTopBatters(
                data.awayVsHomePitcher,
                `${awayAbbr} vs ${homePitcher || 'TBD'}`
              )}
              {renderTopBatters(
                data.homeVsAwayPitcher,
                `${homeAbbr} vs ${awayPitcher || 'TBD'}`
              )}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// LIVE SCOREBOARD — MLB-App-Style
// ══════════════════════════════════════════════════════════════════════════════
const LiveScoreboard = ({ initialGamePk } = {}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── State ────────────────────────────────────────────────────────────────
  const todayStr = useMemo(() => toDateStr(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [ribbonCenter, setRibbonCenter] = useState(todayStr);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showBoxscore, setShowBoxscore] = useState(false);
  const selectedGameRef = useRef(null);
  const [autoOpenHandled, setAutoOpenHandled] = useState(false);

  // ── Date ribbon dates ────────────────────────────────────────────────────
  const ribbonDates = useMemo(() => {
    const count = isMobile ? 5 : 9;
    const half = Math.floor(count / 2);
    return Array.from({ length: count }, (_, i) => addDays(ribbonCenter, i - half));
  }, [ribbonCenter, isMobile]);

  // ── Fetch games ──────────────────────────────────────────────────────────
  const fetchGames = useCallback(async (dateStr, isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const ts = Date.now();
      const res = await apiClient.get(`/v2/mlb-live/schedule/${dateStr}?t=${ts}`);
      const newGames = res.data?.games || [];
      setGames(newGames);

      // Sync selected game with refreshed data
      const sel = selectedGameRef.current;
      if (sel) {
        const updated = newGames.find(g => g.id === sel.id);
        if (updated) { setSelectedGame(updated); selectedGameRef.current = updated; }
      }
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Failed to fetch games:', err);
      // Fallback to Redis for historical dates
      try {
        const res = await gamesApi.getRecentGames({ date: dateStr, limit: 30 });
        setGames(res.games || []);
      } catch { setGames([]); }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + auto-refresh
  useEffect(() => {
    fetchGames(selectedDate, true);
    // Live auto-refresh every 20s for today, 60s otherwise
    const isToday = selectedDate === todayStr;
    const interval = setInterval(() => fetchGames(selectedDate), isToday ? 20000 : 60000);
    return () => clearInterval(interval);
  }, [selectedDate, todayStr, fetchGames]);

  // Auto-open from URL /scores/:gamePk or dashboard ticker
  // If the game isn't on today's schedule (e.g. it's yesterday's), switch to yesterday.
  useEffect(() => {
    if (!initialGamePk || autoOpenHandled) return;
    if (games.length > 0) {
      const match = games.find(g =>
        String(g.gamePk) === String(initialGamePk) || String(g.id) === String(initialGamePk)
      );
      if (match) {
        setSelectedGame(match);
        selectedGameRef.current = match;
        setShowBoxscore(true);
        setAutoOpenHandled(true);
      } else if (selectedDate === todayStr) {
        // Not found today — try yesterday automatically
        const yesterday = addDays(todayStr, -1);
        setSelectedDate(yesterday);
        setRibbonCenter(yesterday);
        // Don't mark handled yet — will retry once yesterday's games load
      } else {
        // Already tried another date; give up silently
        setAutoOpenHandled(true);
      }
    }
  }, [initialGamePk, games, autoOpenHandled, selectedDate, todayStr]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getStatus = useCallback((g) => {
    if (g.isFinal) return 'Final';
    if (g.isLive) return 'Live';
    if (g.statusCode) {
      switch (g.statusCode) {
        case 'S': case 'P': case 'PW': return 'Scheduled';
        case 'I': case 'IR': return 'Live';
        case 'F': case 'O': return 'Final';
        case 'PO': return 'Postponed';
        case 'C': return 'Cancelled';
        default: return g.status || 'Scheduled';
      }
    }
    if (!g.status) return 'Scheduled';
    const s = g.status.toLowerCase();
    if (s.includes('final')) return 'Final';
    if (s.includes('live') || s.includes('progress')) return 'Live';
    if (s.includes('postponed')) return 'Postponed';
    return 'Scheduled';
  }, []);

  const handleGameClick = (game) => {
    setSelectedGame(game); selectedGameRef.current = game; setShowBoxscore(true);
  };

  // Sort: Live first, then Scheduled by time, then Final
  const sortedGames = useMemo(() => {
    const order = { Live: 0, Scheduled: 1, Final: 2, Postponed: 3, Cancelled: 4 };
    return [...games].sort((a, b) => {
      const sa = order[getStatus(a)] ?? 5, sb = order[getStatus(b)] ?? 5;
      if (sa !== sb) return sa - sb;
      return new Date(a.gameTime || 0) - new Date(b.gameTime || 0);
    });
  }, [games, getStatus]);

  // ════════════════════════════════════════════════════════════════════════════
  // DATE RIBBON
  // ════════════════════════════════════════════════════════════════════════════
  const DateRibbon = () => (
    <Box sx={{
      display: 'flex', alignItems: 'center', mb: 2,
      borderBottom: `1px solid ${theme.palette.divider}`, pb: 1,
    }}>
      <IconButton size="small" onClick={() => setRibbonCenter(prev => addDays(prev, isMobile ? -3 : -5))}>
        <ChevronLeft />
      </IconButton>
      <Box sx={{
        display: 'flex', flex: 1, justifyContent: 'center',
        gap: { xs: 0.25, sm: 0.5 }, overflow: 'hidden',
      }}>
        {ribbonDates.map(dateStr => {
          const isSelected = dateStr === selectedDate;
          const isToday = dateStr === todayStr;
          const d = parseLocalDate(dateStr);
          const dayName = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
          const dayNum = d.getDate();
          const monthName = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
          return (
            <Box
              key={dateStr}
              onClick={() => setSelectedDate(dateStr)}
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                px: { xs: 0.75, sm: 1.5 }, py: 0.5, borderRadius: 1.5,
                cursor: 'pointer', minWidth: { xs: 42, sm: 54 },
                transition: 'all 0.15s',
                bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
              }}
            >
              <Typography variant="caption" sx={{
                fontSize: '0.6rem', fontWeight: isToday ? 800 : 600,
                color: isSelected ? 'primary.main' : 'text.secondary',
                lineHeight: 1.2,
              }}>
                {dateStr === todayStr ? 'TODAY' : dayName}
              </Typography>
              <Typography variant="body2" sx={{
                fontWeight: isSelected ? 800 : 600, lineHeight: 1.2,
                color: isSelected ? 'primary.main' : 'text.primary',
                fontSize: { xs: '0.85rem', sm: '1rem' },
              }}>
                {dayNum}
              </Typography>
              <Typography variant="caption" sx={{
                fontSize: '0.55rem',
                color: isSelected ? 'primary.main' : 'text.secondary',
                lineHeight: 1,
              }}>
                {monthName}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <IconButton size="small" onClick={() => setRibbonCenter(prev => addDays(prev, isMobile ? 3 : 5))}>
        <ChevronRight />
      </IconButton>
    </Box>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // GAME CARD — MLB-style
  // ════════════════════════════════════════════════════════════════════════════
  const GameCard = ({ game }) => {
    const status = getStatus(game);
    const isLive = status === 'Live';
    const isFinal = status === 'Final';
    const isScheduled = status === 'Scheduled';
    const awayWon = isFinal && (game.awayScore ?? 0) > (game.homeScore ?? 0);
    const homeWon = isFinal && (game.homeScore ?? 0) > (game.awayScore ?? 0);
    const innings = game.innings || [];
    const totals = game.totals || {};

    // Mini linescore header (only for live/final)
    const LinescoreStrip = () => {
      if (!innings.length) return null;
      return (
        <Box sx={{
          display: 'flex', gap: 0, borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 0.5, mb: 0.75, overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
          <Box sx={{ minWidth: isMobile ? 64 : 80 }} />
          {innings.map(inn => (
            <Typography key={inn.num} variant="caption" sx={{
              minWidth: 18, textAlign: 'center', fontSize: '0.6rem',
              color: 'text.secondary', fontWeight: 600,
            }}>
              {inn.num}
            </Typography>
          ))}
          <Typography variant="caption" sx={{ minWidth: 22, textAlign: 'center', fontSize: '0.6rem', fontWeight: 800, color: 'text.secondary', ml: 0.5 }}>R</Typography>
          <Typography variant="caption" sx={{ minWidth: 22, textAlign: 'center', fontSize: '0.6rem', fontWeight: 600, color: 'text.secondary' }}>H</Typography>
          <Typography variant="caption" sx={{ minWidth: 22, textAlign: 'center', fontSize: '0.6rem', fontWeight: 600, color: 'text.secondary' }}>E</Typography>
        </Box>
      );
    };

    // Team row
    const TeamRow = ({ team, score, probPitcher, probPitcherStats, isWinner, isAway }) => {
      const record = team?.record;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: isMobile ? 64 : 80, flex: '0 0 auto' }}>
            <Avatar src={getTeamLogoUrl(team?.abbreviation)} sx={{ width: 24, height: 24 }}>
              {team?.abbreviation?.substring(0, 2)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{
                fontWeight: isWinner ? 800 : 600, fontSize: '0.85rem',
                color: isWinner ? 'text.primary' : (isFinal && !isWinner ? alpha(theme.palette.text.primary, 0.5) : 'text.primary'),
              }}>
                {team?.abbreviation || 'TBD'}
              </Typography>
              {record && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1 }}>
                  {record.wins}-{record.losses}
                </Typography>
              )}
            </Box>
          </Stack>

          {innings.length > 0 ? (
            <>
              {innings.map(inn => {
                const val = isAway ? inn.away : inn.home;
                return (
                  <Typography key={inn.num} variant="caption" sx={{
                    minWidth: 18, textAlign: 'center', fontSize: '0.7rem',
                    color: val != null ? 'text.primary' : alpha(theme.palette.text.primary, 0.2),
                    fontWeight: val > 0 ? 700 : 400,
                  }}>
                    {val ?? '-'}
                  </Typography>
                );
              })}
              <Typography variant="body2" sx={{
                minWidth: 22, textAlign: 'center', fontWeight: 800, fontSize: '0.85rem', ml: 0.5,
                color: isWinner ? 'text.primary' : (isFinal ? alpha(theme.palette.text.primary, 0.5) : 'text.primary'),
              }}>
                {(isAway ? totals.away?.runs : totals.home?.runs) ?? score ?? '-'}
              </Typography>
              <Typography variant="caption" sx={{ minWidth: 22, textAlign: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>
                {(isAway ? totals.away?.hits : totals.home?.hits) ?? '-'}
              </Typography>
              <Typography variant="caption" sx={{ minWidth: 22, textAlign: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>
                {(isAway ? totals.away?.errors : totals.home?.errors) ?? '-'}
              </Typography>
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
              {probPitcher && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  {probPitcher.split(' ').pop()}
                  {probPitcherStats?.era ? ` (${probPitcherStats.era} ERA)` : ''}
                </Typography>
              )}
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', minWidth: 24, textAlign: 'right' }}>
                {score ?? '-'}
              </Typography>
            </Box>
          )}
        </Box>
      );
    };

    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        <Card
          elevation={0}
          onClick={() => handleGameClick(game)}
          sx={{
            mb: 1, borderRadius: 2, cursor: 'pointer',
            border: `1px solid ${isLive ? alpha(theme.palette.success.main, 0.35) : theme.palette.divider}`,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            display: 'flex',
            '&:hover': { boxShadow: theme.shadows[3], transform: 'translateY(-1px)' },
            transition: 'all 0.15s',
          }}
        >
          {/* Team color left-accent bar */}
          <Box sx={{
            width: 4, flexShrink: 0,
            background: isLive
              ? `linear-gradient(to bottom, ${getTeamColor(game.awayTeam?.abbreviation)}, ${getTeamColor(game.homeTeam?.abbreviation)})`
              : getTeamColor(game.homeTeam?.abbreviation),
            opacity: isLive ? 1 : 0.7,
          }} />
          <CardContent sx={{ flex: 1, px: { xs: 1.5, sm: 2 }, py: 1, '&:last-child': { pb: 1 } }}>
            {/* Status bar */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {isLive && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />}
                <Typography variant="caption" sx={{
                  fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                  color: isLive ? 'success.main' : (isFinal ? 'text.secondary' : 'primary.main'),
                }}>
                  {isLive
                    ? `${game.inningHalf === 'Bottom' ? '\u25BC' : '\u25B2'} ${game.currentInning || game.inning || 'Live'}`
                    : isFinal ? 'Final'
                    : isScheduled ? formatGameTime(game.gameTime)
                    : status}
                </Typography>
              </Stack>

              {isLive && game.outs != null && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/* Standalone base-out diamond */}
                  <svg width="28" height="24" viewBox="0 0 36 30">
                    {/* Second base */}
                    <rect x="13" y="1" width="10" height="10" rx="1.5"
                      transform="rotate(45 18 6)"
                      fill={game.runners?.second ? '#F5A623' : 'transparent'}
                      stroke={alpha(theme.palette.text.primary, 0.28)} strokeWidth="1.5" />
                    {/* Third base */}
                    <rect x="1" y="10" width="10" height="10" rx="1.5"
                      transform="rotate(45 6 15)"
                      fill={game.runners?.third ? '#F5A623' : 'transparent'}
                      stroke={alpha(theme.palette.text.primary, 0.28)} strokeWidth="1.5" />
                    {/* First base */}
                    <rect x="25" y="10" width="10" height="10" rx="1.5"
                      transform="rotate(45 30 15)"
                      fill={game.runners?.first ? '#F5A623' : 'transparent'}
                      stroke={alpha(theme.palette.text.primary, 0.28)} strokeWidth="1.5" />
                    {/* Home plate marker */}
                    <circle cx="18" cy="27" r="2" fill={alpha(theme.palette.text.primary, 0.18)} />
                  </svg>
                  {/* Outs + count */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    <Stack direction="row" spacing="3px">
                      {[0,1,2].map(i => (
                        <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%',
                          bgcolor: i < (game.outs || 0) ? 'warning.main' : alpha(theme.palette.text.primary, 0.12) }} />
                      ))}
                    </Stack>
                    {game.balls != null && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', fontWeight: 600, lineHeight: 1 }}>
                        {game.balls}-{game.strikes}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              )}

              {isScheduled && game.venue && !isMobile && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }} noWrap>
                  {game.venue}
                </Typography>
              )}
            </Stack>

            <LinescoreStrip />

            <TeamRow
              team={game.awayTeam} score={game.awayScore}
              probPitcher={game.probableAwayPitcher}
              probPitcherStats={game.probableAwayPitcherStats}
              isWinner={awayWon} isAway
            />
            <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, my: 0.25 }} />
            <TeamRow
              team={game.homeTeam} score={game.homeScore}
              probPitcher={game.probableHomePitcher}
              probPitcherStats={game.probableHomePitcherStats}
              isWinner={homeWon}
            />

            {isScheduled && game.pregameWinProb != null && (() => {
              const homeWP = Math.round(game.pregameWinProb * 100);
              const awayWP = 100 - homeWP;
              return (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: awayWP >= 50 ? getTeamColor(game.awayTeam?.abbreviation) : 'text.secondary', minWidth: 28 }}>
                    {awayWP}%
                  </Typography>
                  <Box sx={{ flex: 1, height: 3, borderRadius: 2, display: 'flex', overflow: 'hidden', bgcolor: alpha(theme.palette.divider, 0.2) }}>
                    <Box sx={{ width: `${awayWP}%`, bgcolor: alpha(getTeamColor(game.awayTeam?.abbreviation), 0.65) }} />
                    <Box sx={{ width: `${homeWP}%`, bgcolor: alpha(getTeamColor(game.homeTeam?.abbreviation), 0.65) }} />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: homeWP >= 50 ? getTeamColor(game.homeTeam?.abbreviation) : 'text.secondary', minWidth: 28, textAlign: 'right' }}>
                    {homeWP}%
                  </Typography>
                </Stack>
              );
            })()}

            {isScheduled && (game.probableHomePitcher || game.probableAwayPitcher) && (
              <CareerMatchupPanel game={game} />
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  const dateLabel = formatDayLabel(selectedDate, todayStr);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Scores</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {lastUpdate && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {lastUpdate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </Typography>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => fetchGames(selectedDate)} disabled={loading}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <DateRibbon />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {dateLabel}
          {!loading && games.length > 0 && ` \u2014 ${games.length} game${games.length !== 1 ? 's' : ''}`}
        </Typography>
        {selectedDate !== todayStr && (
          <Chip label="Today" size="small" color="primary" variant="outlined"
            onClick={() => { setSelectedDate(todayStr); setRibbonCenter(todayStr); }}
            sx={{ height: 22, fontSize: '0.7rem' }} />
        )}
      </Stack>

      {loading ? (
        <Stack spacing={1}>
          {[1,2,3,4,5].map(i => (
            <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      ) : sortedGames.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
          <AnimatePresence>
            {sortedGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </AnimatePresence>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <SportsBaseball sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.3), mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
            No games {selectedDate === todayStr ? 'today' : `on ${dateLabel}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try a different date above
          </Typography>
        </Box>
      )}

      {selectedGame && (
        <Boxscore
          key={selectedGame.id}
          game={selectedGame}
          open={showBoxscore}
          onClose={() => { setShowBoxscore(false); setSelectedGame(null); selectedGameRef.current = null; }}
          onNavigateToFull={(gameId, date) => { setShowBoxscore(false); navigate(`/boxscore/${gameId}/${date}`); }}
          onLiveDataUpdate={(gameId, live) => {
            // Merge fresh live linescore from boxscore feed back into the GameCard list,
            // so the underlying scoreboard tile updates faster than the schedule poll.
            const ls = live?.linescore;
            if (!ls) return;
            const awayRuns = ls.teams?.away?.runs;
            const homeRuns = ls.teams?.home?.runs;
            const inning = ls.currentInning;
            const inningOrd = ls.currentInningOrdinal;
            const inningState = ls.inningState;
            setGames(prev => prev.map(g => {
              if (g.id !== gameId) return g;
              return {
                ...g,
                awayScore: awayRuns ?? g.awayScore,
                homeScore: homeRuns ?? g.homeScore,
                mlbGameData: { ...(g.mlbGameData || {}), linescore: ls },
                currentInning: inning ?? g.currentInning,
                currentInningOrdinal: inningOrd ?? g.currentInningOrdinal,
                inningState: inningState ?? g.inningState,
                isLive: live.isLive ?? g.isLive,
              };
            }));
            // Also keep selectedGame ref in sync so the Boxscore header shows the live score.
            const sel = selectedGameRef.current;
            if (sel && sel.id === gameId) {
              const updated = {
                ...sel,
                awayScore: awayRuns ?? sel.awayScore,
                homeScore: homeRuns ?? sel.homeScore,
                mlbGameData: { ...(sel.mlbGameData || {}), linescore: ls },
                isLive: live.isLive ?? sel.isLive,
              };
              selectedGameRef.current = updated;
              setSelectedGame(updated);
            }
          }}
        />
      )}
    </Box>
  );
};

export default LiveScoreboard;
